import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import mermaid from 'mermaid';
import type { ReactNode } from 'react';
import './RoadmapPage.css';

interface RoadmapPageProps {
  sourcePath?: string;
}

const DEFAULT_SOURCE_PATH = '/roadmap.md';

let mermaidInitialized = false;

function ensureMermaidInitialized(): void {
  if (mermaidInitialized) {
    return;
  }

  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: 'default',
  });

  mermaidInitialized = true;
}

interface MermaidBlockProps {
  chart: string;
}

interface StructurizrEmbedProps {
  src: string;
  title?: string;
  height?: number;
}

const MermaidBlock: React.FC<MermaidBlockProps> = ({ chart }) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const diagramId = useId().replace(/:/g, '-');

  useEffect(() => {
    let active = true;

    const renderChart = async (): Promise<void> => {
      try {
        ensureMermaidInitialized();
        const { svg: renderedSvg } = await mermaid.render(`roadmap-mermaid-${diagramId}`, chart);
        if (!active) {
          return;
        }
        setSvg(renderedSvg);
        setError('');
      } catch (err) {
        if (!active) {
          return;
        }
        setSvg('');
        setError((err as Error).message || 'Unable to render diagram.');
      }
    };

    void renderChart();

    return () => {
      active = false;
    };
  }, [chart, diagramId]);

  if (error) {
    return (
      <div className="roadmap-page__diagram roadmap-page__diagram--error">
        <p className="roadmap-page__diagram-error">Mermaid render failed: {error}</p>
        <pre>
          <code>{chart}</code>
        </pre>
      </div>
    );
  }

  if (!svg) {
    return <div className="roadmap-page__diagram roadmap-page__diagram--loading">Rendering diagram...</div>;
  }

  return (
    <div
      className="roadmap-page__diagram"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

interface StructurizrEmbedMatch {
  src: string;
  title?: string;
  height?: number;
}

function getTextContent(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextContent).join('');
  }

  if (React.isValidElement<{ children?: ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return '';
}

function parseStructurizrEmbed(children: ReactNode): StructurizrEmbedMatch | null {
  const nodes = React.Children.toArray(children);
  if (nodes.length < 2 || nodes.length > 3) {
    return null;
  }

  const prefix = getTextContent(nodes[0]).trim();
  const suffix = nodes.length === 3 ? getTextContent(nodes[2]).trim() : '';

  if (!prefix.startsWith('!STRUCTURIZR[')) {
    return null;
  }

  const linkNode = nodes[1];
  if (!React.isValidElement<{ href?: string; children?: ReactNode }>(linkNode) || linkNode.type !== 'a') {
    return null;
  }

  const titlePrefixMatch = /^!STRUCTURIZR\[(.*)$/.exec(prefix);
  if (!titlePrefixMatch) {
    return null;
  }

  const titleSuffix = titlePrefixMatch[1];
  const linkText = getTextContent(linkNode.props.children);
  const title = `${titleSuffix}${linkText}`.trim();
  const href = linkNode.props.href;

  if (!href) {
    return null;
  }

  const heightMatch = /^\]\{height=(\d+)\}$/.exec(suffix);
  return {
    src: href,
    title: title || undefined,
    height: heightMatch ? Number(heightMatch[1]) : undefined,
  };
}

const StructurizrEmbed: React.FC<StructurizrEmbedProps> = ({ src, title, height = 720 }) => {
  return (
    <figure className="roadmap-page__embed">
      <iframe
        className="roadmap-page__embed-frame"
        src={src}
        title={title || 'Structurizr diagram'}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        style={{ height }}
      />
      <figcaption className="roadmap-page__embed-caption">
        {title ? <span className="roadmap-page__embed-title">{title}</span> : null}
        <a
          className="roadmap-page__embed-link"
          href={src}
          target="_blank"
          rel="noreferrer"
        >
          Open original
        </a>
      </figcaption>
    </figure>
  );
};

const RoadmapPage: React.FC<RoadmapPageProps> = ({ sourcePath = DEFAULT_SOURCE_PATH }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-structurizr-embed="true"]');
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://static.structurizr.com/js/structurizr-embed.js';
    script.type = 'text/javascript';
    script.async = true;
    script.dataset.structurizrEmbed = 'true';
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadRoadmap = async (): Promise<void> => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(sourcePath, {
          headers: {
            Accept: 'text/markdown, text/plain;q=0.9, */*;q=0.1',
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const markdown = await response.text();
        if (!active) {
          return;
        }

        setContent(markdown);
      } catch (err) {
        if (!active) {
          return;
        }
        setError((err as Error).message || 'Unable to load roadmap.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadRoadmap();

    return () => {
      active = false;
    };
  }, [sourcePath]);

  if (loading) {
    return (
      <section className="roadmap-page" aria-busy="true">
        <div className="roadmap-page__status">Loading roadmap...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="roadmap-page">
        <div className="roadmap-page__status roadmap-page__status--error">
          Failed to load roadmap: {error}
        </div>
      </section>
    );
  }

  return (
    <section className="roadmap-page">
      <div className="roadmap-page__hero">
        <div className="roadmap-page__actions">
          <Link to="/" className="roadmap-page__back-link">
            Back to Dewey
          </Link>
        </div>
        <p className="roadmap-page__subtitle">
          The document below is rendered for convenience from the internal roadmap of the Trimble Context layer, codenamed Dewey.
        </p>
      </div>
      <article className="roadmap-page__content">
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            code(props) {
              const { className, children, ...rest } = props;
              const match = /language-(\w+)/.exec(className || '');
              const code = String(children).replace(/\n$/, '');

              if (match?.[1] === 'mermaid') {
                return <MermaidBlock chart={code} />;
              }

              return (
                <code className={className} {...rest}>
                  {children}
                </code>
              );
            },
            p(props) {
              const parsedEmbed = parseStructurizrEmbed(props.children);

              if (parsedEmbed) {
                return (
                  <StructurizrEmbed
                    src={parsedEmbed.src}
                    title={parsedEmbed.title}
                    height={parsedEmbed.height}
                  />
                );
              }

              return <p>{props.children}</p>;
            },
          }}
        >
          {content}
        </Markdown>
      </article>
    </section>
  );
};

export default RoadmapPage;